import cn from "classnames";
import React, { useEffect, useState } from "react";
import BranchLink from "web/react/components/branch-link/branch-link";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import ProductGalleryCarousel from "web/react/components/product-gallery/product-gallery-carousel/product-gallery-carousel";
import { PRODUCT_PREVIEW_SIZE_GUIDE_OVERLAY_NAME } from "web/react/components/product-preview-size-guide/product-preview-size-guide";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import { RawBranchLink } from "web/script/utils/branch-io";
import history from "web/script/utils/history";
import { ProductPreviewSerializer } from "web/types/serializers";
import styles from "./product-quick-view.css";

export const PRODUCT_QUICK_VIEW_OVERLAY_NAME = "product-quick-view";

const SIZES_KEY = "sizes";
const PROMOTIONS_KEY = "promotions";
const SHIPPING_KEY = "shipping";
const RETAILER_KEY = "retailer";

export default function ProductQuickViewModal(): JSX.Element | null {
    const overlay = useOverlayWatcher(PRODUCT_QUICK_VIEW_OVERLAY_NAME);
    const isOpen = !!overlay;

    // at some point we could probably use suspense for data fetching here
    const { data, isFetching } = useRothko<ProductPreviewSerializer>(
        "modules/product/product_preview",
        { link_id: overlay && overlay.linkId },
        { enabled: isOpen }
    );

    function onClose(): void {
        analytics.event("pdp_preview", "modal_dismissed");
        history.popOverlay(PRODUCT_QUICK_VIEW_OVERLAY_NAME);
    }

    if (isFetching) {
        return null;
    }

    return (
        <GenericStickyModal className={styles.modal} isOpen={isOpen} onClose={onClose}>
            <ProductQuickViewModalInner preview={data!} />
        </GenericStickyModal>
    );
}

interface ProductQuickViewModalInnerProps {
    preview: ProductPreviewSerializer;
}

function ProductQuickViewModalInner({
    preview,
}: ProductQuickViewModalInnerProps): React.ReactElement {
    const [displayDataKey, setDisplayDataKey] = useState<string>("");

    const quickViewInfoModal = environment.getFeature("mg_quick_view_info");

    const tags = {
        sizes: {
            title: gettext("product_card.app_overlay_block.title_size"),
            label: gettext("product.buy_area.sizes_available.label"),
        },
        promotions: {
            title: gettext("product_card.app_overlay_block.title_promotions"),
            label: gettext("product.buy_area.promotions_available.label"),
        },
        shipping: {
            title: gettext("product_card.app_overlay_block.title_shipping"),
            label: gettext("product.buy_area.shipping_info.label"),
        },
        retailer: {
            title: gettext("product_card.app_overlay_block.title_retailer", {
                retailer_name: preview.retailer[0],
            }),
            label: gettext("product.buy_area.retailer_available.label"),
        },
    };

    const branchLink = RawBranchLink.ProductCardGeneralFeed;

    const appDeepLinkURL = new URL(preview?.product_link || "", globals.window.location.href);

    function onSizeGuideClick(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();

        history.pushOverlay(PRODUCT_PREVIEW_SIZE_GUIDE_OVERLAY_NAME, {
            data: { sizeGuideLink: preview.size_guide_link },
        });
    }

    function setTypeOfInfo(): void {
        const { sizes, promotions, shipping } = preview;

        if (sizes.length > 1) {
            analytics.event("app_overlay", "open", SIZES_KEY);
            return setDisplayDataKey(SIZES_KEY);
        }

        if (promotions.length) {
            analytics.event("app_overlay", "open", PROMOTIONS_KEY);
            return setDisplayDataKey(PROMOTIONS_KEY);
        }

        if (shipping.length) {
            analytics.event("app_overlay", "open", SHIPPING_KEY);
            return setDisplayDataKey(SHIPPING_KEY);
        }

        analytics.event("app_overlay", "open", RETAILER_KEY);
        setDisplayDataKey(RETAILER_KEY);
    }

    useEffect(() => {
        if (!displayDataKey) {
            setTypeOfInfo();
        }
    });

    return (
        <div>
            {preview.images && preview.images.length > 0 && (
                <div className={styles.gallery}>
                    {quickViewInfoModal && displayDataKey && (
                        <p className={styles.infoTitle}>{tags[displayDataKey].title}</p>
                    )}
                    <ProductGalleryCarousel
                        analyticsAction={""}
                        settings={{
                            speed: 200,
                            initialSlide: 0,
                            slidesToShow: preview.images.length > 1 ? 1.3 : 1,
                            arrows: false,
                            dots: false,
                            lazyLoad: "progressive",
                            centerMode: false,
                            infinite: true,
                            variableWidth: true,
                            swipe: true,
                            touchMove: true,
                            touchThreshold: 20,
                            draggable: true,
                        }}
                        isFullscreen={false}
                        images={preview.images}
                        hasRelatedProductsSidebar={false}
                    />
                </div>
            )}
            <div className={styles.content}>
                <div className={cn(styles.header, "mb30")}>
                    <h4 className={styles.heading}>
                        {displayDataKey && tags[displayDataKey].label}
                    </h4>
                    {displayDataKey === SIZES_KEY && (
                        <a className={styles.link} onClick={onSizeGuideClick} href="#">
                            {gettext("product.buy_area.view_size_guide")}
                        </a>
                    )}
                </div>
                <div className="mb15">
                    {displayDataKey === SIZES_KEY && <SizeAvailability sizes={preview.sizes} />}
                    {displayDataKey && displayDataKey !== SIZES_KEY && (
                        <QuickViewInfo data={preview[displayDataKey]} dataType={displayDataKey} />
                    )}
                </div>
                <BranchLink
                    title={gettext("app_landing.download_free.button")}
                    branchLink={branchLink}
                    appDeepLinkURL={appDeepLinkURL}
                    primaryButton
                    beforeOnClick={() => {}}
                    showAppIcon
                    className={styles.branchLink}
                    eventLabel={displayDataKey}
                />
            </div>
        </div>
    );
}

interface SizeAvailabilityProps {
    sizes: { label: string; in_stock: boolean }[];
}

export function SizeAvailability({ sizes }: SizeAvailabilityProps): JSX.Element | null {
    return (
        <div className={cn(styles.sizes, "pb20")}>
            {sizes.map((size) => (
                <span
                    className={cn(styles.size, {
                        [styles.outOfStock]: !size.in_stock,
                    })}
                    key={size.label}
                    {...(!size.in_stock && {
                        ["aria-label"]: gettext("product.buy_area.unavailable_size.label", {
                            size: size.label,
                        }),
                    })}
                >
                    {size.label}
                </span>
            ))}
        </div>
    );
}

interface QuickViewInfoProps {
    data: string[];
    dataType: string;
}

export function QuickViewInfo({ data, dataType }: QuickViewInfoProps): JSX.Element | null {
    return (
        <div className={cn(styles.sizes, "pb20")}>
            {dataType !== PROMOTIONS_KEY &&
                data.map((value) => (
                    <span className={cn(styles.size)} key={value}>
                        {value}
                    </span>
                ))}
            {dataType === PROMOTIONS_KEY &&
                data.map((value) => (
                    <span
                        className={cn(styles.size)}
                        key={value}
                        dangerouslySetInnerHTML={{ __html: value }}
                    />
                ))}
        </div>
    );
}
